import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Play, ClipboardPaste, ChevronDown, ChevronUp, MapPin, Clock, Check, X, Edit2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatTime12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

export default function ScheduleManager({ teamId, token, onScheduleLoaded }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addMode, setAddMode] = useState(null); // null, 'paste', 'manual'
  const [expanded, setExpanded] = useState(true);

  // Paste mode
  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  // Manual mode
  const [manualRows, setManualRows] = useState([{ opponent_name: '', date: '', game_time: '', field_location: '', is_home: true }]);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadSchedule = useCallback(async () => {
    if (!teamId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/schedule/${teamId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEntries(data);
        if (onScheduleLoaded) onScheduleLoaded(data);
      }
    } catch {} finally { setLoading(false); }
  }, [teamId, token]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const handleParse = async () => {
    if (!pasteText.trim()) { toast.error('Paste your schedule first'); return; }
    try {
      const res = await fetch(`${API}/api/schedule/parse-paste`, {
        method: 'POST', headers,
        body: JSON.stringify({ team_id: teamId, text: pasteText })
      });
      if (res.ok) {
        const data = await res.json();
        setParsed(data.parsed);
        setShowPreview(true);
        if (data.count === 0) toast.error('Could not parse any games. Try a different format.');
        else toast.success(`Found ${data.count} games!`);
      }
    } catch { toast.error('Parse failed'); }
  };

  const handleSaveParsed = async () => {
    const valid = parsed.filter(p => p.opponent_name && p.date);
    if (!valid.length) { toast.error('No valid entries to save'); return; }
    try {
      const res = await fetch(`${API}/api/schedule/bulk`, {
        method: 'POST', headers,
        body: JSON.stringify({ team_id: teamId, entries: valid })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.count} games added to schedule!`);
        setAddMode(null); setPasteText(''); setParsed([]); setShowPreview(false);
        loadSchedule();
      }
    } catch { toast.error('Save failed'); }
  };

  const handleSaveManual = async () => {
    const valid = manualRows.filter(r => r.opponent_name && r.date);
    if (!valid.length) { toast.error('Fill in at least one game'); return; }
    try {
      const res = await fetch(`${API}/api/schedule/bulk`, {
        method: 'POST', headers,
        body: JSON.stringify({ team_id: teamId, entries: valid })
      });
      if (res.ok) {
        toast.success(`${valid.length} games added!`);
        setAddMode(null);
        setManualRows([{ opponent_name: '', date: '', game_time: '', field_location: '', is_home: true }]);
        loadSchedule();
      }
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (entryId) => {
    try {
      await fetch(`${API}/api/schedule/entries/${entryId}`, { method: 'DELETE', headers });
      setEntries(prev => prev.filter(e => e.id !== entryId));
      toast.success('Removed');
    } catch {}
  };

  const handleActivate = async (entryId) => {
    try {
      const res = await fetch(`${API}/api/schedule/activate/${entryId}`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Game created! Head to the game to start scoring.');
        loadSchedule();
      }
    } catch { toast.error('Failed to activate'); }
  };

  const scheduled = entries.filter(e => e.status === 'scheduled').sort((a, b) => a.date.localeCompare(b.date));
  const active = entries.filter(e => e.status === 'active');
  const today = new Date().toISOString().split('T')[0];

  return (
    <Card className="border-0 shadow-md bg-white" data-testid="schedule-manager">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2">
            <Calendar size={16} className="text-green-600" />
            <CardTitle className="text-sm font-extrabold text-stone-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              SEASON SCHEDULE ({scheduled.length} upcoming)
            </CardTitle>
            {expanded ? <ChevronUp size={14} className="text-stone-400" /> : <ChevronDown size={14} className="text-stone-400" />}
          </button>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="text-xs h-7 border-green-300 text-green-700 hover:bg-green-50"
              data-testid="paste-schedule-btn"
              onClick={() => { setAddMode(addMode === 'paste' ? null : 'paste'); setShowPreview(false); }}>
              <ClipboardPaste size={12} className="mr-1" /> Paste
            </Button>
            <Button size="sm" variant="outline" className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="manual-add-btn"
              onClick={() => setAddMode(addMode === 'manual' ? null : 'manual')}>
              <Plus size={12} className="mr-1" /> Add
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-3">
          {/* Paste Mode */}
          {addMode === 'paste' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="paste-mode">
              <p className="text-xs text-green-800 font-semibold">Paste your schedule below</p>
              <p className="text-[10px] text-green-600">Works with: spreadsheet copy, CSV, or any format like "Apr 15, 3:30 PM, vs Eagles, @ Anderson Field"</p>
              <textarea
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                placeholder={"Apr 15, 3:30 PM, Eagles, Anderson Field\nApr 18, 5:00 PM, Hawks, @ Riverside Park\nApr 22, 6:00 PM, Lions, Home Field"}
                className="w-full h-28 bg-white border border-green-300 rounded text-sm p-2 text-stone-800 font-mono resize-none"
                data-testid="paste-textarea"
              />
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-500 text-white text-xs" onClick={handleParse} data-testid="parse-btn">
                  Parse Schedule
                </Button>
                <Button size="sm" variant="ghost" className="text-xs text-stone-500" onClick={() => setAddMode(null)}>Cancel</Button>
              </div>

              {/* Preview */}
              {showPreview && parsed.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="parse-preview">
                  <p className="text-xs font-semibold text-green-800">Preview ({parsed.length} games found)</p>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {parsed.map((p, i) => (
                      <div key={i} className={cn(
                        "flex items-center gap-2 text-xs px-2 py-1.5 rounded",
                        p.date && p.opponent_name ? "bg-white border border-green-200" : "bg-red-50 border border-red-200"
                      )}>
                        <span className="text-stone-500 w-16 shrink-0">{p.date ? formatDate(p.date) : '?'}</span>
                        <span className="text-stone-500 w-14 shrink-0">{p.game_time ? formatTime12h(p.game_time) : ''}</span>
                        <span className="font-semibold text-stone-800 flex-1">{p.opponent_name || '(unknown)'}</span>
                        <span className="text-stone-400 text-[10px]">{p.field_location || ''}</span>
                        <span className="text-[10px] text-stone-400">{p.is_home ? 'H' : 'A'}</span>
                      </div>
                    ))}
                  </div>
                  <Button size="sm" className="bg-green-600 hover:bg-green-500 text-white text-xs w-full mt-2" onClick={handleSaveParsed} data-testid="save-parsed-btn">
                    <Check size={12} className="mr-1" /> Save {parsed.filter(p => p.date && p.opponent_name).length} Games
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Manual Add Mode */}
          {addMode === 'manual' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid="manual-mode">
              <p className="text-xs text-blue-800 font-semibold">Add games manually</p>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {manualRows.map((row, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <Input value={row.opponent_name} onChange={e => { const r = [...manualRows]; r[i].opponent_name = e.target.value; setManualRows(r); }}
                      placeholder="Opponent" className="col-span-3 text-xs h-8" data-testid={`manual-opponent-${i}`} />
                    <Input type="date" value={row.date} onChange={e => { const r = [...manualRows]; r[i].date = e.target.value; setManualRows(r); }}
                      className="col-span-3 text-xs h-8" data-testid={`manual-date-${i}`} />
                    <Input type="time" value={row.game_time} onChange={e => { const r = [...manualRows]; r[i].game_time = e.target.value; setManualRows(r); }}
                      className="col-span-2 text-xs h-8" data-testid={`manual-time-${i}`} />
                    <Input value={row.field_location} onChange={e => { const r = [...manualRows]; r[i].field_location = e.target.value; setManualRows(r); }}
                      placeholder="Field" className="col-span-3 text-xs h-8" data-testid={`manual-location-${i}`} />
                    <button onClick={() => setManualRows(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 col-span-1 flex justify-center">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="text-xs text-blue-600" onClick={() => setManualRows(prev => [...prev, { opponent_name: '', date: '', game_time: '', field_location: '', is_home: true }])}>
                  <Plus size={12} className="mr-1" /> Add Row
                </Button>
                <div className="flex-1" />
                <Button size="sm" variant="ghost" className="text-xs text-stone-500" onClick={() => setAddMode(null)}>Cancel</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-500 text-white text-xs" onClick={handleSaveManual} data-testid="save-manual-btn">
                  <Check size={12} className="mr-1" /> Save
                </Button>
              </div>
            </div>
          )}

          {/* Schedule List */}
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : scheduled.length === 0 ? (
            <div className="text-center py-4">
              <Calendar size={24} className="mx-auto text-stone-300 mb-1" />
              <p className="text-stone-400 text-xs">No upcoming games scheduled</p>
              <p className="text-stone-300 text-[10px] mt-0.5">Paste or add your team's schedule</p>
            </div>
          ) : (
            <div className="space-y-1" data-testid="schedule-list">
              {scheduled.map(entry => {
                const isToday = entry.date === today;
                const isPast = entry.date < today;
                return (
                  <div key={entry.id} className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all group",
                    isToday ? "bg-green-50 border border-green-300" :
                    isPast ? "bg-stone-50 opacity-60" :
                    "bg-stone-50 hover:bg-stone-100"
                  )} data-testid={`schedule-entry-${entry.id}`}>
                    <div className="w-12 text-center shrink-0">
                      <div className={cn("text-[10px] font-bold uppercase", isToday ? "text-green-600" : "text-stone-400")}>
                        {new Date(entry.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className={cn("text-lg font-extrabold leading-tight", isToday ? "text-green-700" : "text-stone-700")}>
                        {new Date(entry.date + 'T12:00:00').getDate()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-stone-800 truncate">{entry.is_home ? 'vs' : '@'} {entry.opponent_name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-stone-500">
                        {entry.game_time && <span className="flex items-center gap-0.5"><Clock size={8} /> {formatTime12h(entry.game_time)}</span>}
                        {entry.field_location && <span className="flex items-center gap-0.5"><MapPin size={8} /> {entry.field_location}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isToday && (
                        <span className="text-[9px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-bold animate-pulse">TODAY</span>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 opacity-0 group-hover:opacity-100"
                        data-testid={`activate-${entry.id}`}
                        onClick={() => handleActivate(entry.id)}
                        title="Start this game">
                        <Play size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:bg-red-50 opacity-0 group-hover:opacity-100"
                        onClick={() => handleDelete(entry.id)} title="Remove">
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Active games count */}
          {active.length > 0 && (
            <p className="text-[10px] text-stone-400 text-center">{active.length} game(s) already activated from schedule</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
